import { PapiDeviation } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useContext } from 'react';
import { useHeroImage } from './useHeroImage';
import { ScreenSizeContext } from '@wix/da-hooks/pkg/useMeasureElement/ScreenSizeContext';

/**
 *  Important!
 *
 *  - make sure you provide sane defaults
 *  - once built you need to check what the LCP element of the page is, if its the hero
 *    and you are using it for a css background, makes sure you add a hidden image in
 *    the DOM like <img src={hero.src} alt="" width="0" height="0" style={{ display: 'none' }} />
 *    this will allow it to be preloaded by the browser.
 *  - make sure to test perf and LCP
 *  - use the isViewportWidth flag if the hero is fullwidth or close to fullwidth
 *  - watch out for frist visit perf, where we don't know the screensize, you might get double the
 *    images loaded, defaults can help here.
 *
 *  Read useHeroImage comments too.
 *
 *  Sends useHeroImage container dimensions based on passed-in ref
 *  and useHeroImage sends back the appropriate deviation hero Image
 *  for that size.
 * */
export const useHeroImageForContainer = (
  deviation: PapiDeviation | undefined,
  container: React.RefObject<HTMLElement | null>,
  defaults?: {
    isViewportWidth?: boolean;
    width: number;
    mobileWidth: number;
    height: number;
    mobileHeight: number;
  }
) => {
  const { width: screenWidth } = useContext(ScreenSizeContext);
  const isMobile = useContext(MobileContext);
  const { device } = useContext(SessionContext);
  const width =
    isMobile && !device?.isTablet ? defaults?.mobileWidth : defaults?.width;
  const height =
    isMobile && !device?.isTablet ? defaults?.mobileHeight : defaults?.height;

  let containerWidth = container.current
    ? container.current.clientWidth
    : width ?? 0;
  if (defaults?.isViewportWidth && !container.current) {
    containerWidth = screenWidth;
  }

  return useHeroImage(
    deviation,
    containerWidth,
    container.current ? container.current.clientHeight : height ?? 0
  );
};
